<script setup lang="ts">
import { computed, useTemplateRef } from 'vue'
import { useI18n } from 'vue-i18n'
import { useIntersectionObserver } from '@vueuse/core'
import type { MutationFunctionContext } from '@tanstack/vue-query'
import { useQuery, useMutation } from '@/composables/api.ts'
import { useDecisionI18n } from '@/composables/useDecisionI18n'
import { useMessagesStore } from '@/stores/messagesStore'
import { localeKey } from '@/utils/localeKey.ts'
import { useAccess } from '@/composables/useAccess.ts'
import { useAuth } from '@/composables/useAuth.ts'
import type { components } from '@/types/_api'
import mdiEyeOffOutline from '~icons/mdi/eye-off-outline'

type CreditorNotification = components['schemas']['Creditor-Notification']
type Ticket = components['schemas']['Ticket']

// Presentational list — TodoCard owns the infinite query so it can share the
// per-filter counts that drive its tab badges.
const props = defineProps<{
  notifications: CreditorNotification[]
  isLoading: boolean
  isFetchingNextPage: boolean
  hasNextPage: boolean
}>()

const emit = defineEmits<{
  'load-more': []
}>()

const i18n = useI18n()
const { t } = i18n
const { tDecision } = useDecisionI18n()
const { addMessage } = useMessagesStore()
const { canCallMethodAsync, canCallMethod } = useAccess()
const { isSolicitor, isSystemUser } = useAuth()

const loadMoreRef = useTemplateRef('loadMoreRef')

useIntersectionObserver(
  loadMoreRef,
  ([entry]) => {
    if (entry?.isIntersecting && props.hasNextPage && !props.isFetchingNextPage) {
      emit('load-more')
    }
  },
  { rootMargin: '180px 0px 0px 0px' }
)

const ticketIdsToFetch = computed(() => {
  const ids = new Set<string>()
  for (const n of props.notifications) {
    if (n.target_resource === 'tickets' && n.data?.ticket != null) {
      ids.add(n.data.ticket)
    }
  }
  return [...ids]
})

const { data: ticketsData } = useQuery(
  '/tickets',
  computed(() => ({
    getAll: true,
    query: {
      where: { _id: { $in: ticketIdsToFetch.value } },
      projection: { _id: 1, status: 1, channel: 1 },
    },
  })),
  { enabled: computed(() => ticketIdsToFetch.value.length > 0) }
)

const associatedTicketsById = computed<Record<string, Ticket>>(() => {
  const tickets: Record<string, Ticket> = {}
  for (const ticket of ticketsData.value?._items ?? []) {
    tickets[ticket._id] = ticket
  }
  return tickets
})

const canHideNotifications = computed(
  () => !isSolicitor.value && !isSystemUser.value && canCallMethod('patch', '/creditor--notifications/{resource_id}')
)

function isHideableNotification(notification: CreditorNotification): boolean {
  const type = notification.type
  if (type !== 'new_decision' && type !== 'new_ticket_response') {
    return true
  }
  if (notification.target_resource === 'tickets' && notification.data?.ticket != null) {
    const ticket = associatedTicketsById.value[notification.data.ticket]
    if (ticket != null && (ticket.status === 'resolved' || ticket.status === 'closed')) {
      return true
    }
  }
  return false
}

// The TodoCard list reads from `/dashboard--todo-notifications` (a custom POST
// route), so the global mutationCache invalidation — which matches
// `['get', '/creditor--notifications...']` — won't refresh it. Invalidate the
// dashboard route explicitly here.
const notificationMutation = useMutation('patch', '/creditor--notifications/{resource_id}', {
  meta: { notifyOnSuccess: false },
  onSuccess: async (_data: unknown, _vars: unknown, _result: unknown, ctx: MutationFunctionContext) => {
    await ctx.client.invalidateQueries({
      predicate: (query) => {
        const path = query.queryKey[1]
        if (typeof path !== 'string') return false
        return path.startsWith('/dashboard--todo-notifications')
      },
    })
  },
})

async function markAsRead(notification: CreditorNotification) {
  if (
    notification.is_read ||
    isSolicitor.value ||
    isSystemUser.value ||
    !(await canCallMethodAsync('patch', '/creditor--notifications/{resource_id}'))
  ) {
    return
  }

  notificationMutation.mutate({
    path: { resource_id: notification._id },
    header: { 'If-Match': notification._etag },
    // @ts-expect-error Swagger lies
    body: { is_read: true },
  })
}

async function hideNotification(notification: CreditorNotification) {
  if (
    isSolicitor.value ||
    isSystemUser.value ||
    !(await canCallMethodAsync('patch', '/creditor--notifications/{resource_id}'))
  ) {
    return
  }

  await notificationMutation.mutateAsync({
    path: { resource_id: notification._id },
    header: { 'If-Match': notification._etag },
    // @ts-expect-error Swagger lies
    body: { hidden_by_creditor: true },
  })
  addMessage(t('common.notification_hidden'))
}

function getNotificationText(notification: CreditorNotification): string {
  if (notification.type === 'new_decision') {
    const title = tDecision(notification.data, 'decision_title') ?? ''
    const name = notification.data?.debtor_name ?? notification.data?.customer_name ?? ''
    return title && name ? `${title} | ${name}` : title || name || ''
  }
  const key = localeKey(notification.type, ['notifications', 'common'], false, i18n)
  return t(key, notification.data as Record<string, unknown>)
}

function getChipLabel(notification: CreditorNotification): string {
  switch (notification.type) {
    case 'new_decision':
      return t('common.decision')
    case 'new_ticket_response':
      return t('common.tickets')
    case 'reversal_handling':
      return t('common.reversal_handlings')
    default:
      return t('common.activities')
  }
}

function getTicketStatus(notification: CreditorNotification): string | null {
  if (notification.target_resource !== 'tickets' || notification.data?.ticket == null) {
    return null
  }
  const ticket = associatedTicketsById.value[notification.data.ticket]
  if (ticket?.status == null) return null
  return t(`common.${ticket.status}`)
}

function getNotificationRoute(
  notification: CreditorNotification
): { name: string; params?: Record<string, string>; query?: Record<string, string> } | null {
  if (notification.target_resource == null || notification.target_resource_data_key == null) return null

  const targetId = notification.data?.[notification.target_resource_data_key as keyof typeof notification.data]
  if (typeof targetId !== 'string') return null

  switch (notification.target_resource) {
    case 'tickets': {
      const ticket = associatedTicketsById.value[targetId]
      return {
        name: 'Ticket',
        params: { channel: ticket?.channel ?? 'amili', ticketId: targetId },
      }
    }
    case 'decisions':
      return {
        name: 'ItemDisplay',
        params: { resource: 'decisions', resourceId: targetId },
      }
    // ADA-4478: the handling is resolved in the Placera view, not ItemDisplay.
    case 'banking--reversal-handlings':
      return { name: 'ReversalHandling', params: { handlingId: targetId } }
    default: {
      const resource = notification.target_resource.replace(/_/g, '-')
      return {
        name: 'ItemDisplay',
        params: { resource, resourceId: targetId },
      }
    }
  }
}
</script>

<template>
  <div class="h-100 d-flex flex-column">
    <v-progress-linear
      v-if="isLoading"
      indeterminate
    />

    <section
      v-else-if="notifications.length > 0"
      class="flex-grow-1 overflow-auto"
    >
      <article
        v-for="notification in notifications"
        :key="notification._id"
        class="px-6"
      >
        <v-divider />
        <component
          :is="getNotificationRoute(notification) ? 'router-link' : 'div'"
          :to="getNotificationRoute(notification) ?? undefined"
          class="d-flex align-center justify-space-between py-4 text-decoration-none text-body-large text-high-emphasis"
          :class="{ 'cursor-pointer': getNotificationRoute(notification) }"
          @click="markAsRead(notification)"
        >
          <div class="flex-grow-1 overflow-hidden">
            <h4
              class="text-body-large text-truncate mb-2"
              :class="{ 'font-weight-medium': !notification.is_read }"
            >
              {{ getNotificationText(notification) }}
            </h4>
            <div class="d-flex align-center flex-wrap ga-2">
              <v-chip
                color="blue"
                label
                size="x-small"
              >
                {{ getChipLabel(notification) }}
              </v-chip>
              <span class="text-medium-emphasis text-body-small">|</span>
              <span class="text-medium-emphasis text-body-small text-uppercase">{{ notification.creditor_name }}</span>
              <template v-if="getTicketStatus(notification)">
                <span class="text-medium-emphasis text-body-small">|</span>
                <span class="text-medium-emphasis text-body-small text-uppercase">{{
                  getTicketStatus(notification)
                }}</span>
              </template>
            </div>
          </div>

          <div class="d-flex align-center ml-2">
            <v-btn
              v-if="canHideNotifications && isHideableNotification(notification)"
              variant="outlined"
              size="x-small"
              class="mr-2"
              @click.stop.prevent="hideNotification(notification)"
            >
              {{ t('common.hide') }}
              <v-icon
                end
                :icon="mdiEyeOffOutline"
              />
            </v-btn>
            <v-badge
              :color="notification.is_read ? 'clear' : 'brand-primary'"
              dot
              inline
            />
          </div>
        </component>
      </article>

      <div
        v-if="hasNextPage"
        ref="loadMoreRef"
        class="px-6 pb-4"
      >
        <v-divider class="mb-4" />
        <div class="d-flex justify-center">
          <v-btn
            :loading="isFetchingNextPage"
            :disabled="isFetchingNextPage"
            variant="outlined"
            @click="emit('load-more')"
          >
            {{ t('common.show_more') }}
          </v-btn>
        </div>
      </div>
    </section>

    <div
      v-else
      class="d-flex align-center justify-center text-medium-emphasis pa-6 flex-grow-1"
    >
      {{ t('common.todo_empty_state') }}
    </div>
  </div>
</template>
